import React, { useMemo } from 'react';

import {
  CheckIcon, DropletIcon, MinusCircleIcon, QuestionCircleIcon,
} from '@vlabs/icons';
import cn from 'classnames';

import type { Option } from '@vlabs/monokit/shared/ui/options-filter';

import st from './ControlledColorFilter.module.sass';

export interface ControlledColorFilterProps {
  label: string;
  onChange: (value: Option[]) => void;
  options: Option[];
  value: Option[];
}

const VALUES_WITH_ICON = ['undefined', 'other', 'null'];

function ControlledColorFilter({
  label,
  onChange,
  options,
  value,
}: ControlledColorFilterProps): React.JSX.Element {
  const selectedLabels = useMemo(
    () => value.map((option) => option.label.toLowerCase()).join(', '),
    [value],
  );

  const toggleOption = (option: Option) => {
    const isSelected = value.some(({ value: selectedValue }) => selectedValue === option.value);
    if (isSelected) {
      onChange(value.filter(({ value: selectedValue }) => selectedValue !== option.value));
      return;
    }
    onChange([...value, option]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, option: Option) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleOption(option);
  };

  return (
    <div>
      <label className="Subtitle-2">
        {label}
        {!!selectedLabels && ': '}
      </label>
      {!!selectedLabels && <span>{selectedLabels}</span>}

      <div className={st.Divider} />

      <div className={st.ColorsGrid}>
        {options.map((option) => {
          const isSelected = value.some(({ value: selectedValue }) => selectedValue === option.value);
          const withIconValue = VALUES_WITH_ICON.includes(option.value);

          return (
            <div
              aria-label={option.label}
              className={cn(st.ColorIcon, st[`ColorIcon_${option.value}`], {
                [st.ColorIcon_activeBorder]: isSelected && withIconValue,
              })}
              key={option.value}
              onClick={() => toggleOption(option)}
              onKeyDown={(event) => handleKeyDown(event, option)}
              role="button"
              tabIndex={0}
              title={option.label}
            >
              {option.value === 'undefined' && <QuestionCircleIcon className={st.InnerIcon} />}
              {option.value === 'other' && <DropletIcon className={st.InnerIcon} />}
              {option.value === 'null' && <MinusCircleIcon className={st.InnerIcon} />}
              {!!isSelected && !withIconValue && <CheckIcon className={st.InnerIcon} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { ControlledColorFilter };
